import React, { useState } from 'react'
import { PROJECT, ProjectRoles, projectRoleLabel } from 'app-constants'
import { IProjectMember } from 'types'
import api from 'helpers/api'
import useProject from 'hooks/useProject'
import { useHubFailure } from 'hooks/useShownHub'
import Button from 'components/button'
import Dialog from 'components/dialog'
import RadioGroup from 'components/radio-group'
import { BLUE_BUTTON } from 'components/save-buttons'
import { needsSeat, PAID_SEAT_REQUIRED, ROLE_DESCRIPTIONS } from './utils'

/**
 * A role's badge: Current on the member's role, in blue; Paid seat required on Owner and Builder, only for a
 * Member, as an Owner or Builder already has a seat.
 */
const badgeFor = (role: ProjectRoles, current: string) => {
  if (role === current) return { label: 'Current', tone: 'blue' as const }
  if (needsSeat(role) && !needsSeat(current)) return { label: PAID_SEAT_REQUIRED }
  return undefined
}

interface EditRoleDialogProps {
  member: IProjectMember
  /** The roles the person viewing can give this member. */
  roles: ProjectRoles[]
  onClose: () => void
  /** The role was changed. */
  onSaved: () => void
}

/**
 * Changes a member's role, in our dialog: the roles they can be given as choice cards, each saying what it
 * allows, Owner and Builder marked as needing a paid seat, and Save once another is chosen. Failing, it stays
 * open and says why.
 */
const EditRoleDialog: React.FC<EditRoleDialogProps> = ({ member, roles, onClose, onSaved }) => {
  const { project } = useProject()
  const hubFailed = useHubFailure(project.publicId)
  const [role, setRole] = useState<string>(member.role)
  const [saving, setSaving] = useState(false)

  const save = async () => {
    const hubName = project.name
    setSaving(true)
    try {
      await api.updateProjectMemberRole(member.user, project, role as ProjectRoles)
      setSaving(false)
      onSaved()
    } catch (error) {
      setSaving(false)
      hubFailed(
        error,
        project.publicId,
        `${member.user.name}'s role in ${hubName} couldn't be changed. Open its settings to try again.`
      )
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title="Edit Role"
      description={`Choose the role ${member.user.name} has in this ${PROJECT}.`}
      footer={
        <>
          <Button internalType="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            internalType="outline"
            className={BLUE_BUTTON}
            isLoading={saving}
            disabled={role === member.role}
            onClick={save}
          >
            Update Role
          </Button>
        </>
      }
    >
      {/* Named Role for screen readers: the dialog's title already says what it's for. */}
      <RadioGroup
        label="Role"
        options={roles.map((each) => ({
          value: each,
          label: projectRoleLabel(each),
          description: ROLE_DESCRIPTIONS[each],
          badge: badgeFor(each, member.role)
        }))}
        value={role as ProjectRoles}
        onChange={setRole}
      />
    </Dialog>
  )
}

export default EditRoleDialog
